import React, { useState, useEffect, useCallback } from 'react';
import { apiRequest, API_BASE_URL } from '../services/api';

const maskName = (name) => {
  if (!name) return 'Customer';
  if (name.length <= 4) return `${name[0]}**${name[name.length - 1] || ''}`;
  const start = name.slice(0, 2);
  const end = name.slice(-2);
  return `${start}*****${end}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE_URL}${cleanUrl}`;
};

const Stars = ({ rating = 0 }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`w-5 h-5 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

const PublicReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const fetchReviews = useCallback(async (pageToFetch) => {
    setLoading(true);
    try {
      const { data } = await apiRequest(`/public/reviews/?page=${pageToFetch}&page_size=12`);
      setReviews(prev => pageToFetch === 1 ? data.results : [...prev, ...data.results]);
      setHasNext(!!data.next);
      setPage(pageToFetch);
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews(1);
  }, [fetchReviews]);

  return (
    <div className="min-h-screen bg-gray-50 pt-8 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Customer Reviews</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            See what our community of Nigerian entrepreneurs and shoppers are saying about their experience with Checkstall.
          </p>
        </div>

        {loading && reviews.length === 0 ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {reviews.map((review) => (
                <div key={review.id} className="bg-white rounded-2xl shadow-soft p-6 flex flex-col h-full border border-gray-100 hover:shadow-md transition-shadow duration-300">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {maskName(review.display_name)}
                      </h3>
                      {review.location && (
                        <p className="text-sm text-gray-500">{review.location}</p>
                      )}
                    </div>
                    <Stars rating={review.rating} />
                  </div>

                  {review.title && (
                    <h4 className="font-bold text-gray-900 mb-2">{review.title}</h4>
                  )}

                  <p className="text-gray-600 mb-4 flex-grow italic">
                    "{review.comment}"
                  </p>

                  {review.proof_image && (
                    <div 
                      className="relative mt-4 mb-4 rounded-xl overflow-hidden cursor-pointer aspect-video bg-gray-100"
                      onClick={() => setPreviewImage(getMediaUrl(review.proof_image))}
                    >
                      <img 
                        src={getMediaUrl(review.proof_image)} 
                        alt="Review proof" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-opacity flex items-center justify-center">
                        <svg className="w-8 h-8 text-white opacity-0 hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>
                  )}

                  <div className="mt-auto pt-4 border-t border-gray-50 flex justify-between items-center text-xs text-gray-400">
                    <span>{review.product_category || 'General Service'}</span>
                    <span>{formatDate(review.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>

            {hasNext && (
              <div className="mt-12 text-center">
                <button
                  onClick={() => fetchReviews(page + 1)}
                  disabled={loading}
                  className="bg-white border-2 border-primary text-primary px-8 py-3 rounded-2xl font-semibold hover:bg-primary hover:text-white transition-all duration-200 disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Load More Reviews'}
                </button>
              </div>
            )}
            
            {!loading && reviews.length === 0 && (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg">No reviews yet. Be the first to share your experience!</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black bg-opacity-90 flex items-center justify-center p-4 sm:p-8"
          onClick={() => setPreviewImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setPreviewImage(null)}
          >
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l18 18" />
            </svg>
          </button>
          <img 
            src={previewImage} 
            alt="Proof preview" 
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

export default PublicReviews;
